import { TWidget } from '@libs/types';
import React, { ChangeEventHandler, CSSProperties } from 'react';
import { TCoreInputData } from '../types/data';
import { useThemeHook } from '../hooks/themeHook';
import { TCoreCheckboxOptions } from '../types/options';
import { getCommonStyles, getAlignment, mergeWithTheme } from '../utils/styles';
import { buildSwitchCssVars } from './buildSwitchCssVars';
import styles from './switch.module.css';

export type TCoreCheckboxStyleProps = Pick<
    TCoreCheckboxOptions,
    | 'size'
    | 'width'
    | 'height'
    | 'main'
    | 'mainContainer'
    | 'onMain'
    | 'outline'
    | 'surface'
    | 'surfaceContainerHighest'
    | 'onSurface'
    | 'margin'
    | 'alignment'
    | 'disabled'
    | 'hovered'
    | 'pressed'
    | 'borderStroke'
    | 'borderRadius'
    | 'thumbSize'
    | 'thumbSizeChecked'
    | 'thumbOffset'
    | 'thumbOffsetChecked'
>;

export type TCoreSwitchViewProps = {
    id?: string;
    widget?: TWidget<TCoreInputData, TCoreCheckboxOptions>;
    uid?: string;
    isChecked: boolean;
    handleChange: ChangeEventHandler<HTMLInputElement>;
    isDisabled?: boolean;
    isHide?: boolean;
    styleName?: string;
    style?: React.CSSProperties;
    parentStyleNames?: string[];
    styleProps?: TCoreCheckboxStyleProps;
};

const CoreSwitchView = ({
    id,
    widget,
    uid,
    isChecked,
    handleChange,
    isDisabled,
    isHide,
    styleName,
    style,
    parentStyleNames,
    styleProps,
}: TCoreSwitchViewProps) => {
    const resolvedWidget = widget ?? ({
        uid: uid ?? '',
        data: {},
        options: {},
    } as TWidget<TCoreInputData, TCoreCheckboxOptions>);

    const enrichedWidget = {
        ...resolvedWidget,
        options: { ...resolvedWidget.options, style: styleName, parentStyleNames },
    };

    const { palette: themePalette } = useThemeHook(enrichedWidget);
    const palette: TCoreCheckboxOptions = mergeWithTheme(themePalette, styleProps);

    const switchClassName = [styles.switch, isChecked && styles.checked, isDisabled && styles.disabled].filter(Boolean).join(' ');
    const cssVars = buildSwitchCssVars(palette);

    return (
        <div style={{ ...getCommonStyles(palette, isHide), ...getAlignment(palette?.alignment), ...style }}>
            <label className={switchClassName} style={cssVars as CSSProperties}>
                <input
                    id={id}
                    type="checkbox"
                    role="switch"
                    data-test={resolvedWidget.uid}
                    className={styles.input}
                    disabled={isDisabled}
                    checked={isChecked}
                    onChange={handleChange}
                />
                <span className={styles.track} />
                <span className={styles.thumb} />
            </label>
        </div>
    );
};

export { CoreSwitchView };
